import { useEffect, useState } from "react";
import { Link, Navigate, useParams, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { ArrowLeft, Rocket, Sparkles, Check } from "lucide-react";
import { toast } from "sonner";

export default function Promote() {
  const { id } = useParams();
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [place, setPlace] = useState(null);
  const [packages, setPackages] = useState([]);
  const [selected, setSelected] = useState("boost_30");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get(`/places/${id}`).then(r => setPlace(r.data));
    api.get("/promo/packages").then(r => setPackages(r.data));
  }, [id]);

  if (loading) return <div className="pt-32 text-center">Chargement…</div>;
  if (!user) return <Navigate to="/login" replace />;

  const checkout = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.post("/promo/checkout", {
        place_id: id,
        package_id: selected,
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F3F0EA]" data-testid="promote-page">
      <div className="pt-24 max-w-4xl mx-auto px-6 pb-16">
        <Link to="/merchant" className="inline-flex items-center gap-2 text-sm font-semibold text-[#5C6B55] hover:text-[#E26D5C] mb-6" data-testid="promote-back">
          <ArrowLeft size={16}/> Retour
        </Link>
        <div className="rounded-3xl bg-gradient-to-br from-[#FFF9F2] to-[#FBF9F6] border border-[#F4A261]/30 p-8 shadow-sm">
          <div className="uppercase-label text-[#E26D5C] flex items-center gap-1.5"><Rocket size={12}/> Promotion</div>
          <h1 className="font-display font-black text-4xl tracking-tighter mt-1">
            Boostez <span className="text-[#E26D5C]">{place?.name || "votre lieu"}</span>
          </h1>
          <p className="text-[#5C6B55] mt-2 max-w-xl">
            Apparaissez en tête des résultats, sur la carte avec un marqueur coloré et un badge "Sponsorisé". Plus de visibilité = plus de clients.
          </p>

          <div className="grid sm:grid-cols-3 gap-4 mt-8">
            {packages.map((pkg) => {
              const active = selected === pkg.id;
              return (
                <button
                  key={pkg.id}
                  data-testid={`pkg-${pkg.id}`}
                  onClick={() => setSelected(pkg.id)}
                  className={`text-left rounded-2xl p-6 border transition-all ${
                    active
                      ? "bg-white border-[#E26D5C] shadow-md ring-2 ring-[#E26D5C]/30"
                      : "bg-white border-black/5 hover:border-[#E26D5C]/40"
                  }`}
                >
                  <div className="uppercase-label text-[#5C6B55]">{pkg.label}</div>
                  <div className="font-display font-black text-3xl mt-2 text-[#2C3627]">
                    {pkg.amount.toFixed(2)}€
                  </div>
                  <div className="text-xs text-[#5C6B55] mt-1">{pkg.days} jours · ~{(pkg.amount/pkg.days).toFixed(2)}€/jour</div>
                  <ul className="mt-4 space-y-1.5 text-sm">
                    <li className="flex gap-2"><Check size={14} className="text-[#4CAF50] mt-0.5"/>Top des résultats</li>
                    <li className="flex gap-2"><Check size={14} className="text-[#4CAF50] mt-0.5"/>Marqueur coloré sur la carte</li>
                    <li className="flex gap-2"><Check size={14} className="text-[#4CAF50] mt-0.5"/>Badge "Sponsorisé"</li>
                  </ul>
                </button>
              );
            })}
          </div>

          <div className="mt-8 flex flex-wrap items-center gap-4 justify-between">
            <div className="text-sm text-[#5C6B55] flex items-center gap-2">
              <Sparkles size={14} className="text-[#E26D5C]"/>
              Paiement sécurisé via Stripe
            </div>
            <Button data-testid="checkout-btn" onClick={checkout} disabled={submitting}
              className="rounded-full bg-[#E26D5C] hover:bg-[#D15B4A] text-white font-semibold py-6 px-8 shadow-lg shadow-[#E26D5C]/30">
              {submitting ? "Redirection…" : "Payer et activer"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
